<script setup lang="ts">
import { useStorage } from '@vueuse/core'
import { ZhDecisionActions, ZhDecisionButton } from '@zerohuman/ui'
import { computed, ref, watch } from 'vue'
import type { GateDecision, GateRow } from '@/api'
import { gateDecisionDraftKey } from '@/lib/gateDecisionDraft'

/**
 * The choices a gate offers in place of Approve and Reject (`gate.choices`, from the registry the decision
 * endpoint enforces). Each choice is listed with the line saying what it does; the chosen one then asks for
 * what it needs, a note or a date and time, and one button named after it records the decision. Nothing is
 * chosen until the person chooses, so the wrong decision is never one click away.
 * A typed note is the gate's own draft, kept across a reload as every gate's note is. A time is not kept.
 */
const props = defineProps<{ gate: GateRow; busy: boolean }>()
const emit = defineEmits<{
    /** `note` is what the choice asked for: the typed text, or the picked time as ISO 8601. */
    decide: [id: string, decision: GateDecision, note: string, choice: string]
}>()

const choices = computed(() => props.gate.choices ?? [])
const chosenKey = ref<string | null>(null)
const chosen = computed(() => choices.value.find(choice => choice.key === chosenKey.value) ?? null)
const text = useStorage(() => gateDecisionDraftKey(props.gate.id), '', undefined, {
    flush: 'sync',
    writeDefaults: false,
    onError: () => {
        // Keep the field usable when browser storage is unavailable.
    },
})
const time = ref('')
/** A choice belongs to the gate it was made on: a card that moves to another gate starts with none chosen. */
watch(
    () => props.gate.id,
    () => {
        chosenKey.value = null
        time.value = ''
    },
)

/** The picked time as the API takes it: ISO 8601, from the picker's local date and time. Empty until it is one. */
const isoTime = computed(() => {
    const at = new Date(time.value)
    return time.value === '' || Number.isNaN(at.getTime()) ? '' : at.toISOString()
})
const note = computed(() => {
    const input = chosen.value?.input
    if (!input) {
        return ''
    }
    return input.kind === 'datetime' ? isoTime.value : text.value.trim()
})
const ready = computed(() => chosen.value !== null && (!chosen.value.input?.required || note.value !== ''))

function decide(): void {
    if (chosen.value === null || !ready.value) {
        return
    }
    emit('decide', props.gate.id, chosen.value.decision, note.value, chosen.value.key)
}
</script>

<template>
	<fieldset class="space-y-2" :disabled="busy">
		<legend class="mb-2 text-sm font-medium">Choose one</legend>
		<label
			v-for="choice in choices"
			:key="choice.key"
			class="border-border flex cursor-pointer items-start gap-2 rounded-md border p-2"
			:data-choice="choice.key"
		>
			<input v-model="chosenKey" type="radio" class="mt-1" :name="`gate-choice-${gate.id}`" :value="choice.key" />
			<span>
				<span class="block text-sm font-medium">{{ choice.label }}</span>
				<span class="text-muted-foreground block text-sm">{{ choice.hint }}</span>
			</span>
		</label>
	</fieldset>
	<label v-if="chosen?.input" class="block space-y-1 text-sm">
		<span class="text-muted-foreground">{{ chosen.input.label }}</span>
		<input
			v-if="chosen.input.kind === 'datetime'"
			v-model="time"
			type="datetime-local"
			class="border-input block rounded-md border bg-transparent p-2 text-sm"
			:disabled="busy"
		/>
		<textarea
			v-else
			v-model="text"
			rows="3"
			class="border-input w-full rounded-md border bg-transparent p-2 text-sm"
			:disabled="busy"
		/>
	</label>
	<!-- The row every decision card shares: the chosen choice in its decision's tone, then the card's link. -->
	<ZhDecisionActions>
		<template #commands>
			<ZhDecisionButton
				:tone="chosen?.decision === 'reject' ? 'reject' : 'approve'"
				:disabled="busy || !ready"
				@click="decide"
			>
				{{ chosen ? chosen.label : 'Decide' }}
			</ZhDecisionButton>
		</template>
		<template v-if="$slots.link" #link><slot name="link" /></template>
	</ZhDecisionActions>
</template>
